import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles } from '@material-ui/core'
import { TrackChangesCardStyles } from '../types/types'

import { CSSProperties } from 'react'

const cardHighLightStyle = (props: TrackChangesCardStyles): CSSProperties => {
    const borderProperties = props.isOptionGroupTrackChange
        ? '1px solid transparent'
        : props.isAdded
        ? '1px solid #5AD86B'
        : '1px solid #D85A79'

    return {
        border: borderProperties,
        background: '#FFF',
        cursor: 'pointer',
    }
}

/**
 * Track changes card styles
 */
export const useTrackChangesCardStyles = makeStyles((theme) => ({
    '@global': {
        '@keyframes cardBounce': {
            '0%': {
                transform: 'translateY(-10px)',
            },
            '50%': {
                transform: 'translateY(5px)',
            },
            '100%': {
                transform: 'translateY(0)',
            },
        },
    },
    container: () => ({
        marginTop: theme.spacing(2),
    }),
    card: (props: TrackChangesCardStyles) => ({
        visibility: 'hidden',
        pointerEvents: 'none',
        borderRadius: '4px',
        marginBottom: theme.spacing(2),
        backgroundColor: '#E1E6F1',
        border: '1px solid #B5C7EA',
        padding: theme.spacing(1),
        zIndex: 1000,
        boxShadow: ' 0px 1px 3px 0px #A9A9A8',
        position: !props?.isNotDefaultSuggestionCardFilter
            ? 'absolute'
            : 'unset',
        width: pxToRem(295),

        // Initial state for animation
        opacity: 0, // Start with opacity 0
        transform: 'translateY(-30px)', // Start slightly below the final position
        transition:
            'top 0.3s ease-in-out, opacity 0.3s ease-in-out, transform 0.3s ease-in-out', // Smooth transition for top, opacity, and transform

        wordBreak: 'break-word',
        '&:hover': {
            ...cardHighLightStyle(props),
            // Target action icons on card hover using class selectors
            '& .MuiIconButton-root[data-testid="icon-button-accept"]': {
                color: '#0D7B55 !important',
                cursor: 'pointer',
            },
            '& .MuiIconButton-root[data-testid="icon-button-discard"]': {
                color: '#B01111 !important',
                cursor: 'pointer',
            },
            // Also target by class name
            '& $actionIcon': {
                color: '#0D7B55 !important',
                cursor: 'pointer',
            },
            '& $closeIconButton': {
                color: '#B01111 !important',
                cursor: 'pointer',
            },
        },

        // Visible state for animation
        '&.visible': {
            visibility: 'visible',
            pointerEvents: 'all',
            opacity: 1, // Fade in
            transform: 'translateY(0)', // Move to the final position
            animation: 'cardBounce 0.3s ease-in-out', // Apply bounce animation
        },
        '&.card-highlight': {
            transition: 'background-color 0.3s ease' /* Smooth transition */,
            ...cardHighLightStyle(props) /* Apply highlight style */,
        },
    }),
    avatar: {
        height: 32,
        width: 32,
        fontSize: 12,
        borderRadius: 32,
    },
    content: {
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
        padding: theme.spacing(2),
    },
    text: {
        color: '#343434',
        fontSize: 12,
        marginTop: 8,
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    actions: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    userNameContent: {
        fontWeight: 600,
        fontSize: 12,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
        marginTop: '2px',
        lineHeight: '14px',
    },
    timeStampContent: {
        color: '#343434',
        fontSize: 10,
        fontWeight: 400,
    },
    actionButtonsContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    actionButtonsContainerDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
    actionIcon: {
        height: 24,
        width: 24,
        color: '#D3D2D3',
        transition: 'color 0.2s ease-in-out',
        '&.card-highlight-accept-button': {
            cursor: 'pointer',
            color: '#0D7B55',
        },
    },
    closeIconButton: {
        marginLeft: 5,
        height: 24,
        width: 24,
        color: '#D3D2D3',
        transition: 'color 0.2s ease-in-out',
        '&.card-highlight-decline-button': {
            cursor: 'pointer',
            color: '#B01111',
        },
    },
    disabledActionIcon: {
        height: 24,
        width: 24,
        color: '#D3D2D3',
        '&:hover': {
            cursor: 'not-allowed',
            color: '#D3D2D3',
        },
    },
    tooltip: {
        backgroundColor: '#fff',
        color: '#000',
        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.15)',
        // fontSize: '0.875rem',
    },
    arrow: {
        color: '#fff',
    },
    replaceCard: {
        fontSize: 12,
    },
    noChangesText: {
        textAlign: 'center',
        color: ' rgba(0, 0, 0, 0.5)',
        fontSize: pxToRem(14),
    },
    cardStatusLabel: {
        marginTop: 2,
        fontSize: pxToRem(12),
        fontWeight: 400,
        lineHeight: pxToRem(18),
        fontStyle: 'italic',
        color: '#0D7B55',
    },
    rejectedStatusLabel: {
        color: '#B01111',
    },
    cardStatusLabelContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    divider: {
        width: pxToRem(323),
        marginTop: 11,
        marginBottom: 9,
        marginLeft: -7,
        border: '1px solid #DDDDDD',
    },
    userDetailsContainer: {
        marginBottom: 9,
    },
}))
